import React, { useEffect, useState, useRef } from "react";
import { Box, Center, Text, Progress } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

export default function EnglishLoadingWithCompletion({ onComplete, messages, finalMessageDuration, alwaysAnimateFinalStep = false }) {
  const instanceId = useRef(Math.random().toString(36).substr(2, 9));
  
  // Media queries for responsive design
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isSmallMobile = useMediaQuery('(max-width: 480px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1024px)');
  
  // Default messages if none provided
  const defaultMessages = [
    "Fetching data...",
    "Checking details...",
    "Building verification...",
    "Almost ready...",
    "Finalizing..."
  ];
  
  const displayMessages = messages || defaultMessages;

  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const hasStarted = useRef(false);
  const interval = 2000; // 2 seconds per message
  
  // Calculate font size based on device
  const getFontSize = () => {
    if (isSmallMobile) return "24px"; // Small phones
    if (isMobile) return "28px"; // Regular phones
    if (isTablet) return "32px"; // Tablets
    return "20px"; // Desktop (original size)
  };
  
  // Calculate container width based on device
  const getContainerWidth = () => {
    if (isSmallMobile) return "90vw"; // Use viewport width on small phones
    if (isMobile) return "85vw"; // Use viewport width on phones
    if (isTablet) return "600px"; // Fixed width on tablets
    return "500px"; // Wider desktop width to fit messages on one line
  };
  
  // Simplified approach: estimate text wrapping based on character count
  const getLineHeight = (messageText = "") => {
    let baseHeight, maxCharsPerLine;
    
    if (isSmallMobile) {
      baseHeight = 48; // Height per line
      maxCharsPerLine = 25; // Rough estimate for small mobile
    } else if (isMobile) {
      baseHeight = 56; // Height per line  
      maxCharsPerLine = 30; // Rough estimate for mobile
    } else if (isTablet) {
      baseHeight = 64; // Height per line
      maxCharsPerLine = 40; // Rough estimate for tablet
    } else {
      baseHeight = 30; // Desktop stays the same
      maxCharsPerLine = 60; // Desktop can fit more
    }
    
    // For mobile/tablet, estimate lines needed
    if (isMobile || isSmallMobile || isTablet) {
      const estimatedLines = Math.max(1, Math.ceil(messageText.length / maxCharsPerLine));
      const calculatedHeight = baseHeight * estimatedLines;
      return `${calculatedHeight}px`;
    }
    
    return `${baseHeight}px`;
  };
  
  // Calculate container height dynamically based on current and next message
  const getContainerHeight = () => {
    const currentMsg = displayMessages[index] || "";
    const nextMsg = displayMessages[index + 1] || "";
    
    if (isMobile || isSmallMobile || isTablet) {
      const currentHeight = parseInt(getLineHeight(currentMsg));
      const nextHeight = parseInt(getLineHeight(nextMsg)) * 0.5; // Show a larger preview of next
      return `${currentHeight + nextHeight + 20}px`; // Add 20px buffer
    }
    
    return "60px"; // Original for desktop
  };

  // Start the animation only once
  useEffect(() => {
    if (!hasStarted.current) {
      hasStarted.current = true;
    }
  }, []);

  useEffect(() => {
    if (index < displayMessages.length - 1) {
      const timeout = setTimeout(() => {
        setIndex((i) => i + 1);
      }, interval);
      return () => clearTimeout(timeout);
    } else if (index === displayMessages.length - 1 && !isComplete) {
      // When we reach final message, log progress bar state and wait
      const finalInterval = finalMessageDuration || 3000;
      console.log('🎬 FINAL STEP - Progress Bar Should Animate:', {
        message: displayMessages[index],
        isAnimating: true,
        finalDuration: finalInterval + 'ms'
      });
      
      const timeout = setTimeout(() => {
        console.log('✅ Animation Complete - Calling onComplete');
        setIsComplete(true);
        if (onComplete) {
          onComplete();
        }
      }, finalInterval);
      return () => clearTimeout(timeout);
    }
  }, [index, isComplete, onComplete, finalMessageDuration]);

  useEffect(() => {
    // Calculate progress based on current message index
    const progressValue = ((index + 1) / displayMessages.length) * 100;
    setProgress(progressValue);
  }, [index, displayMessages.length]);

  // Debug progress bar animation state
  useEffect(() => {
    if (index === displayMessages.length - 1) {
      console.log('🎯 Progress Bar Debug:', {
        shouldAnimate: index === displayMessages.length - 1,
        shouldStripe: index === displayMessages.length - 1,
        progressValue: 100
      });
    }
  }, [index, displayMessages.length]);

  const isFinalStep = index === displayMessages.length - 1;
  const shouldAnimate = isFinalStep || (alwaysAnimateFinalStep && isFinalStep);

  return (
    <Center style={{ height: "100%", flex: 1 }}>
      <Box sx={{ padding: isMobile ? "1rem" : "2rem" }}>
        <Box 
          sx={{ 
            width: getContainerWidth(), 
            height: getContainerHeight(), // Use responsive height
            position: "relative",
            overflow: "hidden", // Ensure content doesn't spill out
            maskImage: "linear-gradient(to bottom, black 0%, black 75%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(to bottom, black 0%, black 75%, transparent 100%)"
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              transform: `translateY(-${displayMessages.slice(0, index).reduce((acc, msg) => acc + parseInt(getLineHeight(msg)), 0)}px)`,
              transition: "transform 0.6s ease"
            }}
          >
            {displayMessages.map((msg, i) => (
              <Text
                align="center"
                key={msg}
                sx={{ 
                  minHeight: getLineHeight(msg),
                  height: getLineHeight(msg),
                  fontSize: getFontSize(),
                  fontWeight: isMobile ? 500 : 400,
                    opacity: i === index ? 1 : (i === index + 1 ? 0.6 : 0.2),
                  lineHeight: isMobile ? 1.3 : 1.5, // Better line height for readability
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  hyphens: 'auto'
                }}
              >
                {msg}
              </Text>
            ))}
          </Box>
        </Box>
        <Box mt="xl" sx={{ width: getContainerWidth() }}>
            <Progress
              value={isFinalStep ? 100 : progress}
              size={isMobile ? "md" : "sm"}
              radius="xl"
              striped={shouldAnimate}
              animate={shouldAnimate}
              sx={(theme) => ({
                '& .mantine-Progress-bar': {
                  transition: 'width 0.5s ease',
                  ...(shouldAnimate ? {
                    backgroundImage: 'linear-gradient(45deg, rgba(255, 255, 255, 0.15) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.15) 75%, transparent 75%, transparent)',
                    backgroundSize: '1rem 1rem',
                    animation: 'mantine-progress-bar-stripes 1s linear infinite'
                  } : {})
                },
                '@keyframes mantine-progress-bar-stripes': {
                  from: { backgroundPosition: '0 0' },
                  to: { backgroundPosition: '1rem 0' }
                }
              })}
            />
          </Box>
        </Box>
      </Center>
  );
}